import { toDay, toIso, type Day } from './dates';
import { decodeParty, encodeParty, type Party } from './party';
import { type Lang } from './i18n';
import { matchLanguage } from '@shared/index';

/**
 * The deep link, which is a contract with the WordPress plugin.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS WRITTEN AND READ IN ONE FILE
 * ---------------------------------------------------------------------------
 *
 * The widget on an accommodation's page hands the guest off to this app with
 * everything they have already chosen. The plugin generates that URL and this
 * app parses it, which is two implementations of one format in two languages
 * unless the plugin calls `buildEnquiryLink` — so it is exported from the
 * widget bundle for exactly that purpose. A format written by PHP and read by
 * TypeScript drifts on the first parameter anyone adds.
 *
 * ---------------------------------------------------------------------------
 * THE PARAMETERS
 * ---------------------------------------------------------------------------
 *
 *     a       accommodation id — opens straight on that one
 *     from    arrival, YYYY-MM-DD
 *     nights  1..60, the API's own bound
 *     party   adult:2,child:1 — channel roles, see party.ts
 *     lang    nl | en
 *     park    park id, passed on as x-park
 *
 * Short names because these end up in a link a guest may see and occasionally
 * types out, and `accommodationId` buys nothing over `a` in a query string.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT DOES NOT DO
 * ---------------------------------------------------------------------------
 *
 * Nothing about a price, and nothing about availability. A link is written when
 * a guest clicks and read whenever they get round to opening it, which may be
 * the next morning from a different device — and a price that travelled in a
 * query string is a price a guest can edit. Both are recomputed on arrival.
 */

export interface Selection {
  accommodationId?: string;
  arrival?: Day;
  nights?: number;
  party?: Party;
  lang?: Lang;
  parkId?: string;
}

const MAX_NIGHTS = 60;

/** Read a selection off a query string. Anything unrecognised is dropped. */
export function parseSelection(search: string): Selection {
  // A fragment is trimmed first. `window.location.search` never carries one,
  // but a park's enquiry page may be `/boeken/?…#form` and a caller handing the
  // whole tail to this function would otherwise read the last parameter as
  // `en#form` — which fails validation and silently drops a value that was
  // there. `URLSearchParams` does not do this itself.
  const withoutHash = search.split('#')[0];
  const q = new URLSearchParams(withoutHash.startsWith('?') ? withoutHash.slice(1) : withoutHash);
  const out: Selection = {};

  const a = q.get('a');
  if (a && a.trim()) out.accommodationId = a.trim().slice(0, 120);

  const from = q.get('from');
  if (from) {
    const day = toDay(from);
    if (day !== null) out.arrival = day;
  }

  const nights = Number(q.get('nights'));
  if (Number.isInteger(nights) && nights >= 1 && nights <= MAX_NIGHTS) out.nights = nights;

  const party = decodeParty(q.get('party'));
  if (party) out.party = party;

  // A locale counts as a language: a host passing `?lang=nl-NL` means Dutch, and
  // rejecting it for not being exactly `nl` would fall through to the browser —
  // which is the one thing an explicit parameter is there to prevent.
  const lang = matchLanguage<Lang>(q.get('lang'), ['en', 'nl']);
  if (lang) out.lang = lang;

  const park = q.get('park');
  if (park && park.trim()) out.parkId = park.trim().slice(0, 60);

  return out;
}

/** The query string for a selection. Empty values are omitted, not sent blank. */
export function selectionQuery(sel: Selection): string {
  const q = new URLSearchParams();
  if (sel.accommodationId) q.set('a', sel.accommodationId);
  if (sel.arrival !== undefined) q.set('from', toIso(sel.arrival));
  if (sel.nights !== undefined) q.set('nights', String(sel.nights));
  if (sel.party) {
    const encoded = encodeParty(sel.party);
    if (encoded) q.set('party', encoded);
  }
  if (sel.lang) q.set('lang', sel.lang);
  if (sel.parkId) q.set('park', sel.parkId);
  return q.toString();
}

/**
 * The link a widget sends a guest to.
 *
 * `base` may carry its own query and fragment — a park's enquiry page might be
 * `/aanvraag/?utm_source=widget#form` — so it is parsed rather than
 * concatenated, and the selection is merged into whatever was already there.
 */
export function buildEnquiryLink(base: string, sel: Selection): string {
  // A relative base needs an origin to parse against; the result is made
  // relative again below so the link stays origin-independent when it was.
  const absolute = /^[a-z][a-z0-9+.-]*:/i.test(base);
  const url = new URL(base, absolute ? undefined : 'http://x.invalid');
  const merged = new URLSearchParams(url.search);
  for (const [k, v] of new URLSearchParams(selectionQuery(sel))) merged.set(k, v);
  url.search = merged.toString();
  return absolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
}
